import React, { useState, useEffect } from 'react'
import { Button, Form, Input, Select, DatePicker, Card, Popconfirm } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import moment from 'moment'
import StateAutoComplete from '@components/Shared/StateAutoComplete'

const { Option } = Select

const LICENSE_TYPES = [
  { value: 'realtor', label: 'Realtor' },
  { value: 'home_inspector', label: 'Home Inspector' },
  { value: 'insurance_inspector', label: 'Insurance Inspector' },
  { value: 'other', label: 'Other' },
]

const PropertyDataCollectorCredentials = ({ onCredentialsChange, initialCredentials = [] }) => {
  const [credentials, setCredentials] = useState(
    initialCredentials.length > 0
      ? initialCredentials.map((cred) => ({
        ...cred,
        yearObtained: cred.yearObtained ? moment().year(cred.yearObtained).startOf('year') : null,
      }))
      : [{ id: Date.now(), type: '', state: '', number: '', yearObtained: null, otherType: '' }],
  )

  useEffect(() => {
    // Filter out incomplete credentials and serialize dates before passing to parent
    const completeCredentials = credentials
      .filter((cred) => cred.type && cred.state && cred.number && cred.yearObtained)
      .map((cred) => ({
        ...cred,
        yearObtained: cred.yearObtained ? cred.yearObtained.year() : null,
      }))

    onCredentialsChange(completeCredentials)
  }, [credentials, onCredentialsChange])

  const addCredential = () => {
    setCredentials([
      ...credentials,
      { id: Date.now(), type: '', state: '', number: '', yearObtained: null, otherType: '' },
    ])
  }

  const removeCredential = (id) => {
    setCredentials(credentials.filter((cred) => cred.id !== id))
  }

  const updateCredential = (id, field, value) => {
    const newCredentials = credentials.map((cred) => (cred.id === id ? { ...cred, [field]: value } : cred))
    setCredentials(newCredentials)
  }

  const renderCredentialCard = (credential, index) => {
    return (
      <Card
        key={credential.id}
        size="small"
        style={{ marginBottom: '16px' }}
        title={`License ${index + 1}`}
        extra={
          credentials.length > 1 && (
            <Popconfirm
              title="Are you sure you want to remove this license?"
              onConfirm={() => removeCredential(credential.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          )
        }
      >
        <Form.Item label="License Type" required={false}>
          <Select
            placeholder="Select license type"
            value={credential.type}
            onChange={(value) => updateCredential(credential.id, 'type', value)}
          >
            {LICENSE_TYPES.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {credential.type === 'other' && (
          <Form.Item label="Other License Type" required>
            <Input
              placeholder="Please specify"
              value={credential.otherType}
              onChange={(e) => updateCredential(credential.id, 'otherType', e.target.value)}
            />
          </Form.Item>
        )}

        <Form.Item label="State" required={false}>
          <StateAutoComplete
            placeholder="Select state"
            value={credential.state}
            onChange={(value) => updateCredential(credential.id, 'state', value)}
          />
        </Form.Item>

        <Form.Item label="License Number" required={false}>
          <Input
            placeholder="Enter license number"
            value={credential.number}
            onChange={(e) => updateCredential(credential.id, 'number', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Year License Was Obtained" required={false}>
          <DatePicker
            picker="year"
            placeholder="Select year"
            value={credential.yearObtained}
            onChange={(date) => updateCredential(credential.id, 'yearObtained', date)}
            disabledDate={(current) => current && current > moment().endOf('year')}
          />
        </Form.Item>
      </Card>
    )
  }

  return (
    <div className="property-data-collector-credentials">
      <p style={{ color: '#666', marginBottom: '16px' }}>
        While no previous experience is required, adding your licenses and certifications helps us get a more complete
        picture of your real estate background.
      </p>

      {credentials.map((credential, index) => renderCredentialCard(credential, index))}

      <Button
        type="dashed"
        onClick={addCredential}
        icon={<PlusOutlined />}
        size="large"
        style={{ width: '100%', marginTop: '8px' }}
      >
        Add Another License
      </Button>
    </div>
  )
}

// Memoization comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Only care about callback reference changes, ignore initialCredentials during runtime
  const callbackChanged = prevProps.onCredentialsChange !== nextProps.onCredentialsChange
  return !callbackChanged // return true to skip render, false to render
}

export default React.memo(PropertyDataCollectorCredentials, arePropsEqual)
